import {useCallback, useEffect, useMemo, useState} from 'react';
import {useWindowSize} from 'react-use';
import {isEqual} from 'lodash';
import {useRefState} from '@uigstudio/uig-nextjs-helper';

import {PlotDetailsResponse} from '~types/Plots';
import {useFilters} from '~providers/FiltersProvider';
import useFetchPlotsList from '~api/useFetchPlotsList';
import {ApiEndpoints} from '~constants/api';

const columnsBreakpoints = [
    [740, 1],
    [768, 2],
    [1090, 1],
    [1440, 2],
    [1880, 3],
    [2200, 4],
] as const;

export const PAGE_SIZE = 20;

type LandListProps = {
    endpoint: ApiEndpoints;
    withWallet: boolean;
};

export default function useListItems({endpoint, withWallet = false}: LandListProps) {
    const [items, itemsRef, setItems] = useRefState<PlotDetailsResponse[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const {filters, listFiltersWhitelist} = useFilters();
    const {width} = useWindowSize();

    const [filtersRef, setFiltersRef] = useState({
        filters: filters,
        filtersWhitelist: listFiltersWhitelist,
    });

    const {data, isLoading} = useFetchPlotsList(
        endpoint,
        withWallet,
        pageNumber,
        PAGE_SIZE,
        filtersRef.filters,
        filtersRef.filtersWhitelist,
    );

    const numColumns = useMemo(
        () =>
            columnsBreakpoints.find(([breakpoint]) => width < breakpoint)?.[1] || 5,
        [width],
    );

    const hasNextPage = useMemo(
        () => (data ? pageNumber < data.totalPages : true),
        [data],
    );

    const itemCount = useMemo(
        () => (hasNextPage ? items.length + 1 : items.length),
        [data, items],
    );

    const isItemLoaded = useCallback(
        (index: number) => !hasNextPage || index < items.length,
        [hasNextPage, items],
    );

    const loadNextPage = useCallback(() => {
        if (!isLoading) {
            setPageNumber((val) => ++val);
        }
    }, [isLoading, setPageNumber]);

    useEffect(() => {
        if (data?.items) {
            setTotalCount(data.itemCount);
            setItems([...itemsRef.current, ...data.items]);
            setTotalPages(data.totalPages);
        }
    }, [data]);

    useEffect(() => {
        if (
            !isEqual(filtersRef.filters, filters) ||
            !isEqual(filtersRef.filtersWhitelist, listFiltersWhitelist)
        ) {
            setItems([]);
            setPageNumber(1);
            setTotalCount(0);
            setTotalPages(0);
            setFiltersRef({
                filters: filters,
                filtersWhitelist: listFiltersWhitelist,
            });
        }
    }, [filters, listFiltersWhitelist]);

    return {
        data,
        items,
        isLoading,
        itemCount,
        totalCount,
        numColumns,
        isItemLoaded,
        loadNextPage,
        pageNumber,
        setPageNumber,
        totalPages,
    };
}
